import { ZpaAccesses } from "@p0security/integrations-zpa-shared/accesses";
import type {
  ArgsOf,
  Output,
  RequestOf,
} from "@p0security/shared/types/access/index";
import { Access, Delegate } from "@p0security/shared/types/access/index";
import type { EmptyAccesses } from "@p0security/shared/types/lifecycle";
import { mapValues } from "lodash-es";

import { commonAllAccess } from "./all.ts";
import { AwsSshAccesses } from "./aws.ts";
import { AzureSshAccesses } from "./azure.ts";
import { GcloudSshAccesses } from "./gcloud.ts";
import { commonGroupAccess, commonSshAccess } from "./group.ts";
import {
  groupNameParameter,
  networkAccessParameter,
  parentParameter,
  publicKeyParameter,
  regionParameter,
  requiredProviderParameter,
  sessionDestinationParameter,
  sudoParameter,
  zoneParameter,
} from "./parameters.ts";
import { commonParentAccess } from "./parent.ts";
import { SelfHostedSshAccesses } from "./self-hosted.ts";

const parentLabel = "parent";

type WithOptional<T> = { [K in keyof T]: T[K] & { optional: true } };

// None of a provider's own `session.generated` fields apply to every OTHER
// provider, so re-declare them as optional when folding them into the shared
// union below — otherwise their type would falsely claim a field the request
// data may not actually have.
const asOptional = <T extends Record<string, Output>>(
  generated: T
): WithOptional<T> =>
  mapValues(generated, (spec) => ({
    ...spec,
    optional: true,
  })) as WithOptional<T>;

export const UnknownSshAccesses = {
  session: Access({
    ...commonSshAccess,
    maturity: "ga",
    // The SSH integration uses a single `UnknownSshAccesses` object passed to both
    // `commandsOf` and `LifecyclePermissioner`, covering all SSH providers. The lifecycle
    // framework resolves delegation at runtime via `accesses[key].delegation[to]`, so any
    // delegation key that any SSH provider's lifecycle may return from `use()` must be
    // declared here — not only on the provider-specific access (e.g. `SelfHostedSshAccess`),
    // which only serves TypeScript typing of the lifecycle.
    delegation: {
      "zscaler-application-segment": Delegate({
        resource: "zscaler-private-access",
        key: "application-segment",
        accesses: ZpaAccesses,
      }),
    },
    // Pulling in each provider's own `session.generated` keeps this from drifting;
    // fields another provider doesn't populate show "(No value)".
    generated: {
      ...asOptional(AwsSshAccesses.session.generated),
      ...asOptional(GcloudSshAccesses.session.generated),
      ...asOptional(AzureSshAccesses.session.generated),
      ...asOptional(SelfHostedSshAccesses.session.generated),
    },
    parameters: {
      // Because we can't limit options to just one command (p0 ls or p0 request) the --group option
      // also appears on p0 request but it does nothing. Return an undefined block because we don't
      // want to show this as an option in the Slack Bot UI.
      group: groupNameParameter,
      destination: sessionDestinationParameter,
      parent: parentParameter(parentLabel),
      provider: { ...requiredProviderParameter, optional: true },
      publicKey: publicKeyParameter,
      region: regionParameter,
      sudo: sudoParameter,
      zone: zoneParameter,
      // Needed here to type the unknown lifecycle's delegation transforms for Zscaler. The unknown lifecycle is the entry-point for all SSH requests but delegates to provider-specific lifecycles via legacy delegation, which doesn't recurse into self-hosted's lifecycle, so the transforms must live on unknown, and this parameter types them. Modernizing unknown to self-hosted to use modern delegation would let this be removed. See self-hosted/session.ts for full context.
      networkAccess: networkAccessParameter,
    },
    render: {
      locator: "parent",
      policy: "sudo",
      resource: "destination",
    } as const,
  }),
  group: commonGroupAccess(parentLabel),
  all: commonAllAccess,
  parent: commonParentAccess(parentLabel),
} satisfies EmptyAccesses;

export type UnknownSshAccesses = typeof UnknownSshAccesses;
export type UnknownSshAccess = UnknownSshAccesses[keyof UnknownSshAccesses];

export type UnknownSshSessionAccess = UnknownSshAccesses["session"];
export type UnknownSshGroupAccess = UnknownSshAccesses["group"];
export type UnknownSshParentAccess = UnknownSshAccesses["parent"];
export type UnknownSshAllAccess = UnknownSshAccesses["all"];

export type UnknownSshSessionRequest = RequestOf<UnknownSshSessionAccess>;
export type UnknownSshGroupRequest = RequestOf<UnknownSshGroupAccess>;
export type UnknownSshParentRequest = RequestOf<UnknownSshParentAccess>;
export type UnknownSshAllRequest = RequestOf<UnknownSshAllAccess>;
export type UnknownSshRequest = RequestOf<UnknownSshAccess>;

export type UnknownSshSessionGenerated = UnknownSshSessionAccess["generated"];
export type UnknownSshGroupGenerated = UnknownSshGroupAccess["generated"];
export type UnknownSshParentGenerated = UnknownSshParentAccess["generated"];
export type UnknownSshAllGenerated = UnknownSshAllAccess["generated"];

export type UnknownSshArgs = ArgsOf<UnknownSshAccess>;
export type UnknownSshSessionArgs = ArgsOf<UnknownSshSessionAccess>;
export type UnknownSshGroupArgs = ArgsOf<UnknownSshGroupAccess>;
export type UnknownSshParentArgs = ArgsOf<UnknownSshParentAccess>;
export type UnknownSshAllArgs = ArgsOf<UnknownSshAllAccess>;
